import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';
import { useUpgradeStore } from '../stores/upgradeStore';
import { CheckCircle, XCircle, ArrowLeft, Layers, ChevronLeft, ChevronRight } from 'lucide-react';

const ROWS_PER_PAGE = 100;

type ProcessedRow = {
  passport: string;
  hotel: string;
  price: string | number;
  action: 'Accept' | 'Reject';
  status: 'Processing' | 'Success' | 'Error';
  message: string;
  processed: boolean;
};

const BulkOrganizerAnswerStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const { requests, acceptPayment, rejectPayment } = useUpgradeStore();
  
  const [data, setData] = useState<ProcessedRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedIndices, setProcessedIndices] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    accepted: 0,
    rejected: 0,
    errors: 0
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize data from Excel file
  useEffect(() => {
    if (location.state?.fileData) {
      try {
        const fileData = location.state.fileData;
        const workbook = XLSX.read(fileData, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

        const rows = jsonData.map(row => ({
          passport: row.passport_number?.toString().trim() || '',
          hotel: row.hotel_name?.toString().trim() || '',
          price: row.price || '-',
          action: row.status?.toString().toLowerCase() === 'accept' ? 'Accept' : 'Reject',
          status: 'Processing',
          message: '',
          processed: false
        }));

        setData(rows);
        setStats(prev => ({ ...prev, total: rows.length }));
      } catch (error) {
        console.error('Error parsing file:', error);
      }
    }
  }, [location.state]);

  // Process rows one by one
  useEffect(() => {
    if (data.length === 0 || isProcessing) return;
    
    const processRows = async () => {
      setIsProcessing(true);
      
      // Process all rows sequentially
      for (let i = 0; i < data.length; i++) {
        if (processedIndices.has(i)) continue;
        
        setCurrentIndex(i);
        await processRow(i);
        
        // Update progress
        setProgress(((i + 1) / data.length) * 100);
        
        // Small delay to prevent UI freezing
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setIsProcessing(false);
    };
    
    processRows();
  }, [data]);

  // Process a single row
  const processRow = async (index: number) => {
    const row = data[index];
    if (!row || processedIndices.has(index)) return;
    
    // Find matching request
    const matchingRequest = findMatchingRequest(row.passport, row.hotel);
    let updatedStatus = 'Success';
    let updatedMessage = 'Successfully processed';
    
    try {
      if (matchingRequest) {
        // Process the payment based on action
        if (row.action === 'Accept') {
          await acceptPayment(matchingRequest.id);
          setStats(prev => ({ ...prev, accepted: prev.accepted + 1 }));
        } else {
          await rejectPayment(matchingRequest.id);
          setStats(prev => ({ ...prev, rejected: prev.rejected + 1 }));
        }
      } else {
        // If request is truly not found, show that message
        updatedStatus = 'Error';
        updatedMessage = `No requests found for passport ${row.passport}. Please verify the passport number is correct.`;
        setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
      }
    } catch (error: any) {
      console.error(`Error processing row ${index}:`, error);
      updatedStatus = 'Error';
      updatedMessage = error.message || 'An error occurred';
      setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
    }
    
    // Update data
    setData(prevData => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        status: updatedStatus as any,
        message: updatedMessage,
        processed: true
      };
      return newData;
    });
    
    // Mark as processed
    setProcessedIndices(prev => new Set([...prev, index]));
  };

  // Find matching upgrade request
  const findMatchingRequest = (passportNumber: string, hotelName: string) => {
    const normalizedHotel = hotelName.trim();
    
    return Object.values(requests).find(req => {
      return (
        (req.status === 'approved' || req.status === 'payment_pending') && 
        req.passportNumber.trim() === passportNumber.trim() &&
        req.fromHotel.trim() === normalizedHotel
      );
    });
  };

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    return data.slice(startIndex, startIndex + ROWS_PER_PAGE);
  }, [data, currentPage]);

  const totalPages = Math.ceil(data.length / ROWS_PER_PAGE);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'Processing':
        return <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>;
      case 'Error':
        return <XCircle className="text-red-500" size={18} />;
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto p-4 space-y-6"
    >
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">
          {translations?.bulkOrganizerAnswer?.title || "Bulk Payment Response"}
        </h1>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft size={18} />
          <span>{translations?.common?.back || "Back"}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="text-primary-600" size={20} />
            <h3 className="font-medium">Total Requests</h3>
          </div>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={20} />
            <h3 className="font-medium">Accepted</h3>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="text-red-600" size={20} />
            <h3 className="font-medium">Rejected</h3>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="text-yellow-600" size={20} />
            <h3 className="font-medium">Errors</h3>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.errors}</p>
        </div>
      </div>

      {/* Progress Bar */}
      {isProcessing && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium">Processing upgrade responses...</span>
            <span className="text-sm font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-primary-500 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            This may take a moment
          </p>
        </div>
      )}

      {/* Results Table */}
      {data.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hotel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((row, index) => (
                  <tr key={`${row.passport}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.passport}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.hotel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.price} {typeof row.price === 'number' ? 'SAR' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        row.action === 'Accept' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {row.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(row.status)}
                        <span className={`text-sm font-medium ${
                          row.status === 'Success' 
                            ? 'text-green-600' 
                            : row.status === 'Error'
                              ? 'text-red-600'
                              : 'text-yellow-500'
                        }`}>
                          {row.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === 1 
                      ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`mr-1 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * ROWS_PER_PAGE) + 1}</span> to <span className="font-medium">{Math.min(currentPage * ROWS_PER_PAGE, data.length)}</span> of{' '}
                    <span className="font-medium">{data.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 ${
                        currentPage === 1
                          ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'text-gray-500 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={18} />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border ${
                          currentPage === i + 1
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 font-medium'
                            : 'border-gray-300 text-gray-500 hover:bg-gray-50 bg-white'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 ${
                        currentPage === totalPages
                          ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                          : 'text-gray-500 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={18} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default BulkOrganizerAnswerStatusPage;
